// InspectionsPage — list form instances + create new from a template
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Search, Loader2, X, Save, ClipboardList, AlertTriangle, ShieldCheck, Award, Construction, HardHat, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import Pill from "@/components/Pill";
import { InstanceStatusActions } from "@/pages/forms/statusWorkflow";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { useAssignRecipients, AssignRecipientsStep } from "@/components/AssignRecipientsStep";

const STATUS_TONE = { Draft: "amber", Submitted: "blue", Approved: "green", Rejected: "red" };
const FULL_REPORT_CODES = new Set(["PR-CDM-SITE-INSPECTION", "BRPD-INSP", "BRPD-INSP-V3"]);
const BRANDING_CHOICE_CODES = new Set(["PR-CDM-SITE-INSPECTION", "BRPD-INSP", "BRPD-INSP-V3", "SHE-AUDIT", "QA-INSP", "SCAF-INSP"]);

// Count signed-in attendees from any attendee_table answer (Toolbox Talk / Induction).
function attendeeCount(inst) {
    const ans = inst?.answers || {};
    let n = 0;
    for (const v of Object.values(ans)) {
        if (Array.isArray(v) && v.length && typeof v[0] === "object") {
            n += v.filter((r) => r && (r.name || r.signature)).length;
        }
    }
    return n;
}

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin", "site_manager"]);

export default function InspectionsPage() {
    const [rows, setRows] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [projects, setProjects] = useState([]);
    const [sites, setSites] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [loading, setLoading] = useState(true);
    const [dialog, setDialog] = useState(false);
    const [prefill, setPrefill] = useState(null);
    const [presetTemplateId, setPresetTemplateId] = useState("");
    const [pendingTplCode, setPendingTplCode] = useState(null);
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const isSiteManager = user?.role === "site_manager";
    const isClientAdmin = user?.role === "client_admin";
    const dialogTemplates = (isSiteManager || isClientAdmin)
        ? templates.filter((t) => t.completed_by === "client")
        : templates;
    // Site Manager / Client Admin may only change status on client-completed report
    // instances — PR-only report types (SHE Audit, Scaffold, CDM/BRPD, etc.) stay
    // view-only for them even while still in Draft.
    const rowPortalLocked = (r) => {
        if (!isSiteManager && !isClientAdmin) return false;
        const tpl = templates.find((t) => t.id === r.template_id);
        return !tpl || tpl.completed_by !== "client";
    };

    const refresh = useCallback(async () => {
        setLoading(true);
        try {
            const params = portalClientId && !isInternalView ? { client_id: portalClientId } : {};
            const { data } = await api.get("/forms/instances", { params });
            setRows(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not load inspections");
        }
        setLoading(false);
    }, [portalClientId, isInternalView]);

    useEffect(() => {
        refresh();
        api.get("/forms/templates", { params: { status: "published" } }).then((r) => setTemplates(r.data)).catch(() => { });
        api.get("/clients").then((r) => setClients(r.data)).catch(() => { });
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => { });
        api.get("/projects").then((r) => setProjects(r.data)).catch(() => { });
        api.get("/sites").then((r) => setSites(r.data)).catch(() => { });
    }, [refresh]);

    const openNew = (templateId = "") => { setPresetTemplateId(templateId); setDialog(true); };
    const tplIdByCode = (code) => templates.find((t) => t.code === code)?.id || "";

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            setPrefill({
                project_id: searchParams.get("project") || null,
                client_id: searchParams.get("client") || null,
                project_label: searchParams.get("project_label") || null,
            });
            const tplCode = searchParams.get("template");
            if (tplCode) setPendingTplCode(tplCode);
            setDialog(true);
            ["new", "project", "client", "project_label", "template"].forEach((p) => searchParams.delete(p));
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams]);

    // Apply a preset template once templates have loaded (tile deep-links).
    useEffect(() => {
        if (pendingTplCode && templates.length) {
            setPresetTemplateId(tplIdByCode(pendingTplCode));
            setPendingTplCode(null);
        }
    }, [pendingTplCode, templates]);

    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const buildingMap = Object.fromEntries(buildings.map((b) => [b.id, b]));
    const filtered = rows.filter((r) => {
        if (statusFilter !== "All" && r.status !== statusFilter) return false;
        if (q) {
            const hay = `${r.title || ""} ${r.template_name || ""} ${r.template_code || ""} ${r.assignee_name || ""} ${clientMap[r.client_id]?.name || ""}`.toLowerCase();
            if (!hay.includes(q.toLowerCase())) return false;
        }
        return true;
    });

    const todayIso = new Date().toISOString().slice(0, 10);

    const accessors = useMemo(() => ({
        inspection: (r) => r.title || r.template_name || "",
        client: (r) => clientMap[r.client_id]?.name || "",
        assignee_name: (r) => r.assignee_name || "",
        due_date: (r) => r.due_date || "",
        status: (r) => r.status,
    }), [clientMap]); // eslint-disable-line react-hooks/exhaustive-deps
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, { accessors });

    return (
        <div data-testid="inspections-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                        style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Projects & Delivery
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Audits & Inspections</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} forms · {rows.filter((r) => r.status === "Draft").length} drafts ·{" "}
                        <span className="font-semibold text-[#C68A1C]">
                            {rows.filter((r) => r.status === "Draft" && r.due_date && r.due_date < todayIso).length} overdue
                        </span>
                    </p>
                </div>
                {canEdit && !isSiteManager && (
                    <button data-testid="inspection-new-btn" onClick={() => openNew("")}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        <Plus size={15} /> New inspection
                    </button>
                )}
            </div>

            {canEdit && (isSiteManager || isClientAdmin) && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="inspection-quick-launch">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mr-1">Quick start:</span>
                    {tplIdByCode("WEEKLY-HS") && (
                        <button data-testid="quick-new-weekly-hs" onClick={() => openNew(tplIdByCode("WEEKLY-HS"))}
                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                            <ClipboardList size={14} style={{ color: "var(--brand-accent,#C75A1B)" }} /> Start weekly inspection
                        </button>
                    )}
                    <button data-testid="quick-new-qa" onClick={() => openNew(tplIdByCode("QA-INSP"))}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                        <Award size={14} style={{ color: "var(--brand-accent,#C75A1B)" }} /> QA Checklist
                    </button>
                </div>
            )}

            {canEdit && !isSiteManager && !isClientAdmin && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="inspection-quick-launch">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-[#9A968F] mr-1">Quick start:</span>
                    <button data-testid="quick-new-she" onClick={() => openNew(tplIdByCode("SHE-AUDIT"))}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                        <ShieldCheck size={14} style={{ color: "var(--brand-accent,#C75A1B)" }} /> New SHE Audit
                    </button>
                    <button data-testid="quick-new-qa" onClick={() => openNew(tplIdByCode("QA-INSP"))}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                        <Award size={14} style={{ color: "var(--brand-accent,#C75A1B)" }} /> New QA Inspection
                    </button>
                    <button data-testid="quick-new-scaffold" onClick={() => openNew(tplIdByCode("SCAF-INSP"))}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                        <Construction size={14} style={{ color: "var(--brand-accent,#C75A1B)" }} /> New Scaffold Inspection
                    </button>
                    <button data-testid="quick-new-cdm" onClick={() => openNew(tplIdByCode("PR-CDM-SITE-INSPECTION"))}
                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#2D2D2D" }}>
                        <HardHat size={14} style={{ color: "var(--brand-accent,#C75A1B)" }} /> New CDM Audit
                    </button>
                </div>
            )}

            <div className="flex items-center gap-3 flex-wrap">
                <div className="relative">
                    <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9A968F]" />
                    <input data-testid="inspection-search" placeholder="Search inspections…"
                        value={q} onChange={(e) => setQ(e.target.value)}
                        className="h-9 pl-8 pr-3 rounded-lg border border-[#E5E0DA] bg-white text-sm w-72 focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)]" />
                </div>
                <select data-testid="inspection-status-filter" value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm">
                    {["All", "Draft", "Submitted", "Approved", "Rejected"].map((s) => (
                        <option key={s} value={s}>{s === "All" ? "All statuses" : s}</option>
                    ))}
                </select>
            </div>

            <div className="rounded-2xl bg-white border border-[#E5E0DA] overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-[#F5F3F0] text-left text-[11px] uppercase tracking-wider text-[#6F6A64] border-b border-[#E5E0DA]">
                        <tr>
                            <SortableTh myKey="inspection" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="inspections-th-name" className="group">Inspection</SortableTh>
                            <SortableTh myKey="client" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="inspections-th-client" className="group">Client / Building</SortableTh>
                            <SortableTh myKey="assignee_name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="inspections-th-assignee" className="group">Assignee</SortableTh>
                            <SortableTh myKey="due_date" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="inspections-th-due" className="group">Due</SortableTh>
                            <SortableTh myKey="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="inspections-th-status" className="group">Status</SortableTh>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={5} className="text-center py-10 text-[#9A968F]">
                                <Loader2 size={16} className="inline animate-spin mr-2" />Loading…
                            </td></tr>
                        ) : sorted.length === 0 ? (
                            <tr><td colSpan={5} className="text-center py-10 text-[#9A968F]">No inspections match.</td></tr>
                        ) : sorted.map((r) => {
                            const overdue = r.status === "Draft" && r.due_date && r.due_date < todayIso;
                            return (
                                <tr key={r.id} data-testid={`inspection-row-${r.id}`}
                                    className="border-b border-[#F0EDE8] hover:bg-[#F5F3F0] cursor-pointer"
                                    onClick={() => navigate(`/app/inspections/${r.id}`)}>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center gap-2">
                                            {overdue && <AlertTriangle size={13} className="text-red-600" />}
                                            <ClipboardList size={13} className="text-[#9A968F]" />
                                            <div>
                                                <div className="font-semibold text-[#1F1F1F] flex items-center gap-2">
                                                    {r.title || r.template_name}
                                                    {attendeeCount(r) > 0 && (
                                                        <span data-testid={`attendee-count-${r.id}`}
                                                            className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full"
                                                            style={{ background: "#EAF3EC", color: "#2E7D4F" }}>
                                                            <Users size={11} />{attendeeCount(r)}
                                                        </span>
                                                    )}
                                                    {r.evidence_status === "outstanding" && (
                                                        <span data-testid={`evidence-flag-${r.id}`}
                                                            className="inline-flex items-center gap-1 text-[10.5px] font-semibold px-1.5 py-0.5 rounded-full"
                                                            style={{ background: "#FBE9E7", color: "#B33A2E" }}>
                                                            <AlertTriangle size={11} />Essential documents not received
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="text-[11px] font-mono text-[#9A968F]">{r.template_code}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-4 py-3 text-[#6F6A64]">
                                        <div>{clientMap[r.client_id]?.name || "—"}</div>
                                        {r.building_id && <div className="text-[11px]">{buildingMap[r.building_id]?.name || ""}</div>}
                                    </td>
                                    <td className="px-4 py-3 text-[#6F6A64]">{r.assignee_name || "—"}</td>
                                    <td className="px-4 py-3 text-[#6F6A64]">{r.due_date || "—"}</td>
                                    <td className="px-4 py-3">
                                        <div className="flex items-center justify-between gap-2">
                                            <Pill tone={STATUS_TONE[r.status] || "neutral"}>{r.status}</Pill>
                                            {canEdit && !rowPortalLocked(r) && (
                                                <span onClick={(e) => e.stopPropagation()}>
                                                    <InstanceStatusActions instance={r} role={user?.role} variant="menu" size="sm" onChanged={refresh} />
                                                </span>
                                            )}
                                            <span onClick={(e) => e.stopPropagation()}>
                                                <AdminDeleteButton endpoint={`/forms/instances/${r.id}`} label="inspection"
                                                    testId={`inspection-delete-${r.id}`} onDeleted={refresh} />
                                            </span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {dialog && (
                <NewInspectionDialog
                    key={presetTemplateId || "new"}
                    templates={dialogTemplates} clients={clients} buildings={buildings} projects={projects} sites={sites}
                    prefill={prefill} presetTemplateId={presetTemplateId}
                    portalClientId={!isInternalView ? portalClientId : null}
                    onProjectCreated={(p) => setProjects((prev) => [...prev, p])}
                    onProjectUpdated={(p) => setProjects((prev) => prev.map((x) => (x.id === p.id ? p : x)))}
                    onSiteCreated={(s) => setSites((prev) => [...prev, s])}
                    onSiteUpdated={(s) => setSites((prev) => prev.map((x) => (x.id === s.id ? { ...x, ...s } : x)))}
                    onClose={() => { setDialog(false); setPrefill(null); setPresetTemplateId(""); }}
                    onCreated={(inst) => { setDialog(false); setPrefill(null); setPresetTemplateId(""); navigate(`/app/inspections/${inst.id}`); }} />
            )}
        </div>
    );
}

function NewInspectionDialog({ templates, clients, buildings, projects, sites, prefill, presetTemplateId, portalClientId, onProjectCreated, onProjectUpdated, onSiteCreated, onSiteUpdated, onClose, onCreated }) {
    const { user } = useAuth();
    const [form, setForm] = useState({
        template_id: presetTemplateId || templates[0]?.id || "",
        client_id: prefill?.client_id || portalClientId || clients[0]?.id || "",
        project_id: prefill?.project_id || "",
        site_id: "",
        building_id: "",
        title: "",
        due_date: "",
        report_branding: "client_portal",
    });
    const [saving, setSaving] = useState(false);
    const [addressInput, setAddressInput] = useState("");

    const clientProjects = projects.filter((p) => !form.client_id || p.client_id === form.client_id);
    const selectedProject = projects.find((p) => p.id === form.project_id);
    const projectSiteIds = selectedProject ? (selectedProject.site_ids || (selectedProject.site_id ? [selectedProject.site_id] : [])) : null;
    // Sites: when a project is chosen → only its sites; else all sites for the client
    const availableSites = sites.filter((s) => {
        if (form.client_id && s.client_id !== form.client_id) return false;
        if (projectSiteIds) return projectSiteIds.includes(s.id);
        return true;
    });
    const filteredBuildings = buildings.filter((b) => {
        if (form.client_id && b.client_id !== form.client_id) return false;
        if (form.site_id) return b.site_id === form.site_id;
        return true;
    });

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));
    const assign = useAssignRecipients(form.client_id);
    const visibleTemplates = templates.filter((t) => !(t.hidden_for_client_ids || []).includes(form.client_id));
    const isFullReportTpl = FULL_REPORT_CODES.has(templates.find((t) => t.id === form.template_id)?.code);
    const showBrandingChoice = BRANDING_CHOICE_CODES.has(templates.find((t) => t.id === form.template_id)?.code);
    const selectedSite = sites.find((s) => s.id === form.site_id);
    // CDM/BRPD: a site is optional, but if one's picked and it has no address on file,
    // offer to add it — purely optional, never blocks creating the report.
    const addressMissing = isFullReportTpl && !!form.site_id && !(selectedSite?.address || "").trim();

    const [quickNew, setQuickNew] = useState(null); // 'project' | 'site' | null
    const [quickName, setQuickName] = useState("");
    const [quickAddress, setQuickAddress] = useState("");
    const [quickBusy, setQuickBusy] = useState(false);

    async function createQuickProject() {
        if (!quickName.trim()) { toast.error("Project name is required"); return; }
        setQuickBusy(true);
        try {
            const { data } = await api.post("/projects", { client_id: form.client_id, name: quickName.trim() });
            onProjectCreated(data);
            set({ project_id: data.id, site_id: "" });
            setQuickNew(null); setQuickName("");
            toast.success("Project created");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create project");
        }
        setQuickBusy(false);
    }

    async function createQuickSite() {
        if (!quickName.trim()) { toast.error("Site name is required"); return; }
        setQuickBusy(true);
        try {
            const { data } = await api.post("/sites", { client_id: form.client_id, name: quickName.trim(), address: quickAddress.trim() || undefined });
            onSiteCreated(data);
            // If a project is already selected, link the new site to it straight away —
            // otherwise it's filtered out of the project-scoped dropdown and the one we
            // just created appears to "vanish".
            if (form.project_id) {
                const proj = projects.find((p) => p.id === form.project_id);
                const existingSiteIds = proj?.site_ids || (proj?.site_id ? [proj.site_id] : []);
                const nextSiteIds = [...new Set([...existingSiteIds, data.id])];
                const { data: updatedProj } = await api.patch(`/projects/${form.project_id}`, { site_ids: nextSiteIds });
                onProjectUpdated(updatedProj);
            }
            set({ site_id: data.id });
            setQuickNew(null); setQuickName(""); setQuickAddress("");
            toast.success("Site created");
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create site");
        }
        setQuickBusy(false);
    }

    // If the selected client retires the currently-picked template, fall back to the first still-visible one.
    useEffect(() => {
        if (form.template_id && !visibleTemplates.some((t) => t.id === form.template_id)) {
            set({ template_id: visibleTemplates[0]?.id || "" });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.client_id]);

    // Don't carry a typed address over to a different site selection.
    useEffect(() => { setAddressInput(""); }, [form.site_id]);

    async function submit() {
        if (!form.template_id || !form.client_id) {
            toast.error("Template and client are required");
            return;
        }
        if (isFullReportTpl && !form.project_id) {
            toast.error("Select a project for this report");
            return;
        }
        if (form.project_id && !form.site_id && !isFullReportTpl) {
            toast.error("Select a site for this project inspection");
            return;
        }
        setSaving(true);
        try {
            // Optional: if a site is missing its address and the inspector typed one in,
            // save it now — but never block report creation if they left it blank or it
            // didn't validate.
            if (addressMissing && addressInput.trim()) {
                try {
                    const { data: addrData } = await api.patch(`/forms/sites/${form.site_id}/address`, { address: addressInput.trim() });
                    onSiteUpdated(addrData);
                } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the address — continuing without it");
                }
            }
            const payload = { ...form };
            ["building_id", "title", "assignee_name", "due_date", "project_id", "site_id"].forEach((k) => {
                if (!payload[k]) delete payload[k];
            });
            if (assign.recipients.length) payload.assigned_recipients = assign.recipients;
            if (assign.message.trim()) payload.assigned_message = assign.message.trim();
            const { data } = await api.post("/forms/instances", payload);
            toast.success("Inspection created");
            onCreated(data);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not create");
        }
        setSaving(false);
    }

    const tplName = templates.find((t) => t.id === form.template_id)?.name;

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="new-inspection-dialog">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">{tplName ? `New ${tplName}` : "New inspection"}</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                <div className="p-5 space-y-3 flex-1 overflow-y-auto">
                    <Field label="Template *">
                        <select data-testid="new-inspection-template" value={form.template_id}
                            onChange={(e) => set({ template_id: e.target.value })} className={inputCls}>
                            <option value="">— Select template —</option>
                            {visibleTemplates.map((t) => <option key={t.id} value={t.id}>{t.name} · {t.code}</option>)}
                        </select>
                    </Field>
                    {showBrandingChoice && (
                        <Field label="Report branding">
                            <div className="flex gap-2" data-testid="new-inspection-branding">
                                {[["client_portal", "Client Portal"], ["priority_risk", "Priority Risk"]].map(([val, lbl]) => (
                                    <button key={val} type="button" data-testid={`new-inspection-branding-${val}`}
                                        onClick={() => set({ report_branding: val })}
                                        className="flex-1 px-3 py-2 rounded-lg text-[12.5px] font-semibold border transition-colors"
                                        style={form.report_branding === val
                                            ? { background: "var(--brand-accent, #C75A1B)", color: "#fff", borderColor: "var(--brand-accent, #C75A1B)" }
                                            : { background: "#fff", color: "#6F6A64", borderColor: "#E5E0DA" }}>
                                        {lbl}
                                    </button>
                                ))}
                            </div>
                            <p className="text-[10.5px] text-[#9A968F] mt-1">
                                {form.report_branding === "priority_risk"
                                    ? "Uses Priority Risk's own colours on the cover and throughout the report."
                                    : "Uses this client's own brand colours and logo on the cover and throughout the report."}
                            </p>
                        </Field>
                    )}
                    <Field label="Client *">
                        <select data-testid="new-inspection-client" value={form.client_id}
                            onChange={(e) => set({ client_id: e.target.value, project_id: "", site_id: "", building_id: "" })}
                            className={inputCls} disabled={!!portalClientId}>
                            <option value="">— Select —</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label={
                            <span className="flex items-center justify-between">
                                {isFullReportTpl ? "Project *" : "Project (optional)"}
                                {isFullReportTpl && (
                                    <button type="button" data-testid="new-inspection-quick-project"
                                        onClick={() => { setQuickNew("project"); setQuickName(""); }}
                                        className="text-[10.5px] font-bold normal-case tracking-normal" style={{ color: "var(--brand-accent, #C75A1B)" }}>
                                        + New
                                    </button>
                                )}
                            </span>
                        }>
                            <select data-testid="new-inspection-project" value={form.project_id}
                                onChange={(e) => set({ project_id: e.target.value, site_id: "", building_id: "" })} className={inputCls}>
                                <option value="">— No project —</option>
                                {clientProjects.map((p) => <option key={p.id} value={p.id}>{p.name}{p.reference ? ` (${p.reference})` : ""}</option>)}
                            </select>
                        </Field>
                        <Field label={
                            <span className="flex items-center justify-between">
                                {isFullReportTpl ? "Site (optional)" : (form.project_id ? "Site *" : "Site (optional)")}
                                {isFullReportTpl && (
                                    <button type="button" data-testid="new-inspection-quick-site"
                                        onClick={() => { setQuickNew("site"); setQuickName(""); setQuickAddress(""); }}
                                        className="text-[10.5px] font-bold normal-case tracking-normal" style={{ color: "var(--brand-accent, #C75A1B)" }}>
                                        + New
                                    </button>
                                )}
                            </span>
                        }>
                            <select data-testid="new-inspection-site" value={form.site_id}
                                onChange={(e) => set({ site_id: e.target.value, building_id: "" })} className={inputCls}>
                                <option value="">{form.project_id ? "— Select site —" : "— None —"}</option>
                                {availableSites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                            </select>
                        </Field>
                    </div>
                    {quickNew && (
                        <div data-testid="new-inspection-quick-create" className="rounded-lg border border-[#E5E0DA] bg-[#FBF9F6] p-3 space-y-2">
                            <p className="text-[12px] font-semibold text-[#1F1F1F]">New {quickNew === "project" ? "project" : "site"}</p>
                            <input data-testid="new-inspection-quick-name" value={quickName} onChange={(e) => setQuickName(e.target.value)}
                                placeholder={quickNew === "project" ? "Project name" : "Site name"} className={inputCls} />
                            {quickNew === "site" && (
                                <input data-testid="new-inspection-quick-address" value={quickAddress} onChange={(e) => setQuickAddress(e.target.value)}
                                    placeholder="Full address incl. postcode (can confirm later on the report)" className={inputCls} />
                            )}
                            <div className="flex items-center justify-end gap-2">
                                <button type="button" onClick={() => setQuickNew(null)} className="px-2.5 py-1.5 text-[12.5px] text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                                <button type="button" data-testid="new-inspection-quick-create-save"
                                    onClick={quickNew === "project" ? createQuickProject : createQuickSite} disabled={quickBusy}
                                    className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                                    style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                    {quickBusy ? <Loader2 size={12} className="animate-spin" /> : null} Create
                                </button>
                            </div>
                        </div>
                    )}
                    {addressMissing && (
                        <Field label="Site address (optional)">
                            <input data-testid="new-inspection-site-address" value={addressInput}
                                onChange={(e) => setAddressInput(e.target.value)}
                                placeholder={`No address on file for "${selectedSite?.name}" — add it now, or leave blank and add it later`}
                                className={inputCls} />
                        </Field>
                    )}
                    {form.project_id && projectSiteIds && projectSiteIds.length === 0 && (
                        <p className="text-[11.5px] flex items-center gap-1" style={{ color: "#B33A2E" }}>
                            <AlertTriangle size={12} /> This project has no sites yet. Add one from the project's "Sites & Buildings" panel first.
                        </p>
                    )}
                    <Field label="Building / address (optional)">
                        <select data-testid="new-inspection-building" value={form.building_id}
                            onChange={(e) => set({ building_id: e.target.value })} className={inputCls} disabled={!!form.project_id && !form.site_id}>
                            <option value="">{form.site_id ? "— Whole site —" : "— None —"}</option>
                            {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                        </select>
                    </Field>
                    <Field label="Title">
                        <input data-testid="new-inspection-title" value={form.title}
                            onChange={(e) => set({ title: e.target.value })}
                            placeholder="auto-generated if blank" className={inputCls} />
                    </Field>
                    <div className="grid grid-cols-2 gap-3 items-end">
                        <div className="text-[11.5px]" style={{ color: "#9A968F" }} data-testid="new-inspection-assignor">
                            Assigned by <span className="font-semibold" style={{ color: "#6F6A64" }}>{user?.name}</span> (you)
                        </div>
                        <Field label="Due date">
                            <input type="date" value={form.due_date}
                                onChange={(e) => set({ due_date: e.target.value })} className={inputCls} />
                        </Field>
                    </div>
                    <AssignRecipientsStep state={assign} testIdPrefix="new-inspection-assign"
                        description="Choose who needs to COMPLETE this — leave blank to complete it yourself. They'll be notified straight away with a link, get a task in their Actions, and automatically receive the finished report once it's done." />
                </div>
                <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                    <button onClick={onClose} className="px-3 py-2 text-sm text-[#6F6A64] hover:text-[#1F1F1F]">Cancel</button>
                    <button data-testid="new-inspection-save" onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-2 text-sm font-semibold px-3.5 py-2 rounded-lg text-white disabled:opacity-60"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Create
                    </button>
                </div>
            </div>
        </div>
    );
}

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm focus:outline-none focus:border-[var(--brand-accent,_#C75A1B)] disabled:bg-[#F6F4F0]";
function Field({ label, children }) {
    return (
        <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-[#6F6A64] mb-1">{label}</label>
            {children}
        </div>
    );
}
